<template>
  <div class="sider-nav-container">
    <nav class="category">
      <div class="category-header">
        <h3>{{ $t('mu-lu') }}</h3>
      </div>
      <ul class="category-list">
        <CategoryItem
          v-for="item in categories"
          :key="item.id"
          :item="item"
          :active-id="activeId"
          :scroll-into-section="scrollIntoSection"
          :no-dropdown="noDropdown"
        />
      </ul>
    </nav>
    <div class="trail" :style="{ backgroundImage: `url(${trailBg})` }">
      <p class="font-dt w-full">
        {{ $t('yong-you-yan') }}<br />
        {{ $t('chuang-zuo-ni-de-3d-shu-zi-ren-ai-shi-pin') }}
      </p>
      <button class="trail-btn" @click="handleTry" :style="trailBtnStyle">
        {{ $t('xin-yong-hu-mian-fei-shi-yong') }}
        <ArrowRight class="text-[22px]" v-if="isChinese()" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isChinese } from '@/utils/area';
import ArrowRight from '~/assets/svg/icon-arrow-right.svg?component';
import { getFeUrl } from '~/utils/url';
import type { ICategoryItem } from '../types';
import CategoryItem from './category-item.vue';

interface Props {
  categories?: ICategoryItem[];
  activeId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  categories: () => [],
  activeId: () => '',
});

const trailBtnStyle = computed(() => {
  return {
    fontSize: isChinese() ? '20px' : '18px',
  };
});

const trailBg = getFeUrl('/youyan-platform/png/bg-blog-trail.png?x-oss-process=image/format,webp');

const noDropdown = computed(() => {
  return props.categories.every(v => !v.children.length);
});

const scrollIntoSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const handleTry = () => {
  const userInfo = useUserInfo();
  if (!userInfo.value) {
    navigateTo(
      `/login?redirect=${encodeURIComponent(encodeURIComponent(`${location.origin}/workspace`))}`,
      {
        external: true,
        open: {
          target: '_blank',
        },
      },
    );
  } else {
    window.open('/workspace', '_blank');
  }
};
</script>

<style lang="scss" scoped>
.sider-nav-container {
  @apply sticky top-0 bottom-12 w-[320px] min-h-[624px]  items-center gap-6 py-12;
  display: inline-flex;
  flex-direction: column;
  max-height: calc(100vh - 62px);
  margin-top: -48px;
  // align-self: start;
  .category {
    position: relative;
    width: 320px;
    padding: 24px 4px 24px 24px;
    border-radius: 8px;
    background-color: var(--x-back-3);
    overflow: hidden;
    max-height: calc(100% - 232px); /* 减去 trail 高度和 gap */
    display: flex;
    flex-direction: column;
    .category-header {
      margin-bottom: 24px;
      flex-shrink: 0;
      h3 {
        @apply my-0 text-[var(--x-text-1)] text-[18px];
        font-weight: 400;
        line-height: 26px;
      }
    }
    .category-list {
      width: 100%;
      flex: 1;
      min-height: 0;
      max-height: calc(100% - 50px);
      padding-right: 14px;
      overflow-y: auto; /* 确保可以滚动 */

      /* 重新显示滚动条 */
      scrollbar-width: 6px; /* Firefox */
      -ms-overflow-style: auto; /* IE and Edge */

      &::-webkit-scrollbar {
        width: 6px;
        display: block;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: var(--x-back-5);
        border-radius: 4px;

        &:hover {
          background: var(--x-back-7);
        }
      }
    }
  }

  .trail {
    @apply flex-col items-center w-full h-[200px] py-8 px-6 gap-6;
    background-size: cover;
    background-repeat: no-repeat;
    border-radius: 8px;
    overflow: hidden;

    p {
      color: var(--x-purple-7, #5650dc);
      text-align: justify;
      font-family: 'dt-font';
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: 32px;
      /* 133.333% */
      letter-spacing: -0.24px;
      margin: 0;
    }

    .trail-btn {
      @apply flex-center w-full;
      background: var(--x-back-1);
      border: 1px solid var(--x-back-5);
      border-radius: 8px;
      color: var(--x-text-3);
      font-size: 20px;
      line-height: normal;
      font-weight: 400;
      text-align: center;
      box-shadow: 0 2px 2px 0 rgba(18, 25, 38, 0.06);
      height: 48px;

      &:hover {
        background-color: var(--x-back-3);
      }
    }
  }
}
</style>
